import { TouchableOpacity, Modal, Image, Text, View, Platform } from "react-native";
import React, { useMemo } from "react";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { useNavigation } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import { AppScreens } from "../../../Utility/AppScreens";
import Avatar from "../../../../Components/choose-avatar-components/Avatar";
import WinnerBottomSheet from "./WinnerBottomSheet";

export default function WinnerModal({
  closeWinnerModal,
  openWinnerModal,
  eventDetails,
  winnerDetails,
}) {
  const styles = useStyles();
  const navigation = useNavigation();
  const deviceSize = useDeviceSize();

  // Check if mobile device
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Belt exchange rates (credits per belt)
  const beltCreditRates = {
    Bronze: 500,
    Silver: 5000,
    Gold: 50000,
    Platinum: 500000,
  };

  // Belt sell rates (dollar worth per belt)
  const beltSellRates = {
    Bronze: 5,
    Silver: 50,
    Gold: 500,
    Platinum: 5000,
  };

  const medalsData = useMemo(() => {
    const values: Record<string, number> = {
      Coins: 0,
      Bronze: 0,
      Silver: 0,
      Gold: 0,
      Platinum: 0,
    };
    (eventDetails?.stakes || []).forEach((stake: any) => {
      if (stake?.type && typeof stake.quantity === "number" && stake.quantity >= 0) {
        values[stake.type] = stake.quantity;
      }
    });
    return [
      { id: 1, name: "Coin", image: AppImages.icCoin, value: values.Coins },
      { id: 2, name: "Bronze", image: AppImages.icBronzeBelt, value: values.Bronze },
      { id: 3, name: "Silver", image: AppImages.icSilverBelt, value: values.Silver },
      { id: 4, name: "Gold", image: AppImages.icGoldBelt, value: values.Gold },
      { id: 5, name: "Platinum", image: AppImages.icPlatinumBelt, value: values.Platinum },
    ].filter((medal) => medal.value > 0);
  }, [eventDetails?.stakes]);

  // Calculate belt credits worth
  const beltCreditsWorth = useMemo(() => {
    let totalCredits = 0;
    let totalWorth = 0;
    let beltDescription = "";

    medalsData.forEach((medal) => {
      if (medal.name !== "Coin") {
        const creditValue = beltCreditRates[medal.name as keyof typeof beltCreditRates] || 0;
        const sellValue = beltSellRates[medal.name as keyof typeof beltSellRates] || 0;

        totalCredits += medal.value * creditValue;
        totalWorth += medal.value * sellValue;

        if (medal.value > 0) {
          beltDescription += beltDescription ? ", " : "";
          beltDescription += `${medal.value} ${medal.name} Belt${medal.value > 1 ? 's' : ''}`;
        }
      }
    });

    return { totalCredits, totalWorth, beltDescription };
  }, [medalsData]);

  // Use bottom sheet on mobile, modal on desktop
  if (isMobile) {
    return (
      <WinnerBottomSheet
        visible={openWinnerModal}
        onClose={closeWinnerModal}
        eventDetails={eventDetails}
        winnerDetails={winnerDetails}
      />
    );
  }

  return (
    <Modal transparent={true} visible={openWinnerModal}>
      <View style={styles.container}>
        <View style={styles.mainView}>
          {/* Winner and Close button in one row */}
          <View style={styles.upperHeading}>
            <Text style={styles.winnerText}>Winner</Text>
            <TouchableOpacity
              onPress={() => {
                navigateToScreen(navigation, AppScreens.Home);
                closeWinnerModal();
              }}
              style={{
                backgroundColor: AppColors.grayColor_393939,
                width: 28,
                height: 28,
                borderRadius: 10,
              }}
            >
              <Image
                source={AppImages.icCancel}
                style={{
                  height: 12,
                  width: 12,
                  alignSelf: "center",
                  marginVertical: 7,
                }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>

          {/* Separator */}
          <View style={styles.separator} />

          {/* Green box with winner name */}
          <View style={styles.winnerNameBox}>
            <Text style={styles.winnerNameText}>
              {winnerDetails?.username || "Unknown"} Won
            </Text>
          </View>

          {/* Main content area */}
          <View style={styles.midPartContainer}>
            {/* Left side - User info */}
            <View style={styles.midLeftConatiner}>
              <View style={styles.playerView}>
                <Avatar
                  variant="upper"
                  src={
                    winnerDetails?.profilePicture ||
                    "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                  }
                />
                <Text style={styles.usernameText}>
                  {winnerDetails?.username || "Unknown"}
                </Text>
              </View>
            </View>

            {/* Right side - Gift credits and medals */}
            <View style={styles.midRightConatiner}>
              <View style={styles.creditsRow}>
                <Text style={styles.creditsValue}>
                  {winnerDetails?.credit ?? 0}
                </Text>
                <Text style={styles.giftCreditsLabel}>Gift Credits</Text>
              </View>
              <View style={styles.silverBeltView}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  {medalsData.map((medal) => (
                    <View
                      key={medal.id}
                      style={{ alignItems: "center", marginRight: 10 }}
                    >
                      <Image
                        //@ts-ignore
                        source={medal?.image}
                        style={{
                          width: 34,
                          height: 34,
                          resizeMode: "contain",
                        }}
                      />
                      <Text
                        style={[
                          AppFontStyle.REGULAR_ANYBODY_14,
                          { marginVertical: 3 },
                        ]}
                      >
                        {medal.value}
                      </Text>
                      <Text style={[AppFontStyle.REGULAR_ANYBODY_10, {}]}>
                        {medal.name}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Belt credits worth and pound value in one row */}
              {beltCreditsWorth.totalCredits > 0 && (
                <View style={styles.worthContainer}>
                  <Text style={styles.beltWorthText}>
                    {beltCreditsWorth.beltDescription}:{" "}
                    <Text style={styles.beltWorthBold}>
                      worth {beltCreditsWorth.totalCredits} credits
                    </Text>
                  </Text>
                  <View style={styles.poundBox}>
                    <Text style={styles.poundText}>
                      ${beltCreditsWorth.totalWorth}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </View>

          {/* Bottom buttons */}
          <View style={styles.buttonContainer}>
            {/* Hidden "Go to event" button */}
            {/* <CommonButton
              title="Go to event"
              onTap={() => {
                navigateToScreen(navigation, AppScreens.PastEvent, {
                  eventID: eventDetails?.eventID,
                });
                closeWinnerModal();
              }}
              buttonStyle={styles.btnGoToEvent}
              textStyle={[AppFontStyle.BOLD_ANYBODY_14, {}]}
            /> */}
            <LinearGradient
              colors={["#DC2626", "#991B1B"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.btnFeedbackGradient}
            >
              <TouchableOpacity
                style={styles.btnFeedback}
                onPress={() => {
                  // Open WhatsApp link in new tab
                  if (Platform.OS === 'web') {
                    window.open('https://chat.whatsapp.com/J6ntLXPLDpYAeSmSQ7AgTG?mode=wwt', '_blank');
                  }
                }}
              >
                <Text style={[AppFontStyle.BOLD_ANYBODY_14, {}]}>
                  Give us feedback
                </Text>
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      backgroundColor: "#111827",
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      width: "40%",
      alignSelf: "center",
      paddingVertical: 20,
      paddingHorizontal: 20,
    },
    upperHeading: {
      flexDirection: "row",
      marginBottom: 15,
      justifyContent: "space-between",
      alignItems: "center",
    },
    separator: {
      height: 1,
      backgroundColor: AppColors.grayColor_333333,
      marginVertical: 10,
    },
    creditsRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    creditsValue: {
      ...AppFontStyle.BOLD_ANYBODY_28,
    },
    winnerNameBox: {
      height: 59,
      paddingVertical: 12,
      paddingHorizontal: 24,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: AppColors.appGreenColor_51AF07,
      borderRadius: 7,
      marginVertical: 15,
    },
    winnerNameText: {
      ...AppFontStyle.BOLD_ANYBODY_32,
      fontWeight: "700",
      lineHeight: 40,
      color: "#FFF",
    },
    usernameText: {
      ...AppFontStyle.BOLD_ANYBODY_18,
      textAlign: "center",
      marginTop: 10,
      color: "#FFF",
    },
    giftCreditsLabel: {
      ...AppFontStyle.BOLD_ANYBODY_14,
    },
    buttonContainer: {
      flexDirection: "row",
      gap: 15,
      marginTop: 20,
      justifyContent: "flex-end",
    },
    btnFeedback: {
      height: 45,
      paddingVertical: 10,
      paddingHorizontal: 24,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 6,
      width: "100%",
    },
    btnFeedbackGradient: {
      borderRadius: 6,
      height: 45,
      justifyContent: "center",
      alignItems: "center",
      minWidth: 200,
    },
    btnGoToEvent: {
      height: 45,
      paddingVertical: 10,
      paddingHorizontal: 24,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 6,
    },
    btnGoToEventGradient: {
      flex: 1,
      borderRadius: 6,
      height: 45,
      justifyContent: "center",
      alignItems: "center",
    },
    midPartContainer: {
      flexDirection: "row",
      gap: 40,
      marginVertical: 15,
      justifyContent: "space-between",
    },
    midLeftConatiner: {
      flexDirection: "column",
      alignItems: "center",
    },
    midRightConatiner: {
      flexDirection: "column",
      flex: 1,
    },
    winnerText: {
      ...AppFontStyle.BOLD_ANYBODY_32,
      fontWeight: "800",
      lineHeight: 36.96,
    },
    worthContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 15,
      gap: 15,
    },
    beltWorthText: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      flex: 1,
    },
    beltWorthBold: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      fontWeight: "700",
    },
    poundBox: {
      backgroundColor: "#374151",
      borderRadius: 8,
      paddingVertical: 10,
      paddingHorizontal: 20,
      minWidth: 80,
      alignItems: "center",
    },
    poundText: {
      ...AppFontStyle.BOLD_ANYBODY_22,
      fontWeight: "700",
    },
    silverBeltView: {
      backgroundColor: "rgba(31, 41, 55, 0.50)",
      borderColor: "#374151",
      borderWidth: 0.73,
      borderRadius: 7.296,
      paddingLeft: 10,
      marginTop: 10,
      marginBottom: 20,
      paddingBottom: 10,
    },
    playerView: {
      marginVertical: 10,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 4,
      width: 161,
      height: 168,
      padding: 13.126,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      mainView: {
        width: "90%",
        backgroundColor: "#111827",
        borderRadius: 8,
        borderWidth: 1.6,
        borderColor: "rgba(239, 68, 68, 0.50)",
        alignSelf: "center",
        paddingVertical: 15,
        paddingHorizontal: 15,
      },

      midPartContainer: {
        flexDirection: "column",
        gap: 15,
        alignItems: "center",
        marginVertical: 10,
      },
      silverBeltView: {
        backgroundColor: "rgba(31, 41, 55, 0.50)",
        borderColor: "#374151",
        borderWidth: 0.73,
        borderRadius: 7.296,
        paddingLeft: 10,
        marginTop: 0,
        marginBottom: 0,
        paddingBottom: 10,
      },
      container: {
        flex: 1,
        backgroundColor: AppColors.blackTrans_000000a8,
        justifyContent: "center",
        alignItems: "center",
      },

      upperHeading: {
        flexDirection: "row",
        marginBottom: 5,
        justifyContent: "space-between",
        alignItems: "center",
      },
      separator: {
        height: 1,
        backgroundColor: AppColors.grayColor_333333,
        marginVertical: 8,
      },
      creditsRow: {
        justifyContent: "space-between",
        marginBottom: 8,
      },
      creditsValue: {
        ...AppFontStyle.BOLD_ANYBODY_20,
      },
      winnerNameBox: {
        height: 50,
        paddingVertical: 10,
        paddingHorizontal: 20,
        marginVertical: 10,
      },
      winnerNameText: {
        ...AppFontStyle.BOLD_ANYBODY_24,
        fontWeight: "700",
        lineHeight: 32,
      },
      usernameText: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        marginTop: 8,
      },
      giftCreditsLabel: {
        ...AppFontStyle.BOLD_ANYBODY_12,
      },
      worthContainer: {
        flexDirection: "column",
        gap: 10,
        marginTop: 12,
        alignItems: "flex-start",
      },
      beltWorthText: {
        ...AppFontStyle.REGULAR_ANYBODY_12,
      },
      beltWorthBold: {
        ...AppFontStyle.BOLD_ANYBODY_12,
      },
      poundBox: {
        paddingVertical: 8,
        paddingHorizontal: 15,
        minWidth: 60,
        alignSelf: "center",
      },
      poundText: {
        ...AppFontStyle.BOLD_ANYBODY_18,
      },
      buttonContainer: {
        flexDirection: "column",
        gap: 10,
        marginTop: 15,
        justifyContent: "center",
      },
      btnFeedback: {
        height: 40,
        paddingVertical: 8,
      },
      btnFeedbackGradient: {
        height: 40,
        minWidth: "auto",
        width: "100%",
      },
      btnGoToEvent: {
        height: 40,
        paddingVertical: 8,
      },
      btnGoToEventGradient: {
        height: 40,
      },
      midLeftConatiner: {
        flexDirection: "column",
        alignItems: "center",
      },

      midRightConatiner: {
        flexDirection: "column",
        flex: 1,
      },
      winnerText: {
        ...AppFontStyle.BOLD_ANYBODY_24,
        fontWeight: "800",
        lineHeight: 27.72,
      },

      playerView: {
        marginVertical: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        width: 130,
        height: 140,
        padding: 10,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    [DEVICE_SIZES.SM]: {
      mainView: {
        width: "75%",
      },
      midPartContainer: {
        flexDirection: "row",
        gap: 30,
      },
      playerView: {
        width: 150,
        height: 160,
      },
      buttonContainer: {
        flexDirection: "row",
      },
    },
    [DEVICE_SIZES.MD]: {
      mainView: {
        width: "70%",
      },
      midPartContainer: {
        gap: 35,
      },
    },
    [DEVICE_SIZES.LG]: {
      mainView: {
        width: "55%",
      },
      midPartContainer: {
        gap: 40,
      },
    },
  }
);
